/**
 * Route guard: redirects away if the current user doesn't hold a required
 * role. This is the UI-layer half of the access model — the real
 * enforcement is RLS (supabase/migrations/0003_rls_policies.sql), this
 * component exists so the experience reads as confident and intentional
 * rather than "click a button, get a database error."
 */

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import type { UserRole } from '../domain/types';

interface RequireRoleProps {
  allow: UserRole[];
  children: ReactNode;
}

export function RequireRole({ allow, children }: RequireRoleProps) {
  const { session, profile, role, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-ivory-100">
        <p className="font-body text-ink-400">Loading…</p>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // A signed-in session with no profile is now a genuine anomaly (every
  // account gets one automatically — see migration 0008's
  // handle_new_auth_user trigger), not the common case it used to be.
  // This must NOT redirect to "/" — "/" requires a role via this exact
  // same guard, so a null role would bounce straight back here in an
  // infinite loop. Show a clear, actionable dead end instead.
  if (!profile) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-ivory-100 px-6 text-center">
        <p className="font-body text-sm text-ink-500">
          Your account doesn&rsquo;t have a profile set up yet. Please ask your venue
          administrator for help, or try signing out and back in.
        </p>
        <button
          onClick={() => signOut()}
          className="font-body text-sm text-evergreen-600 underline-offset-2 hover:underline"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (!role || !allow.includes(role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
